
import { useState, KeyboardEvent } from 'react';

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  className?: string;
}

export default function TagInput({ 
  tags, 
  onChange, 
  placeholder = "Add tags...", 
  maxTags = 10,
  className = "" 
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions] = useState([
    'discussion', 'question', 'help', 'review', 'recommendation',
    'spoiler', 'theory', 'analysis', 'news', 'episode-discussion',
    'character-analysis', 'plot-theory', 'behind-the-scenes', 'cast',
    'romance', 'comedy', 'drama', 'thriller', 'historical', 'fantasy'
  ]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < maxTags) {
      onChange([...tags, trimmedTag]);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const filteredSuggestions = suggestions.filter(
    suggestion => 
      suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tags.includes(suggestion) &&
      inputValue.length > 0
  ).slice(0, 5);

  return (
    <div className={`relative ${className}`}>
      <div className="border border-gray-300 rounded-lg p-2 min-h-[42px] flex flex-wrap gap-1 items-center focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500">
        {/* Existing Tags */}
        {tags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-sm px-2 py-1 rounded-full"
          >
            #{tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="text-purple-600 hover:text-purple-800 ml-1"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </span>
        ))}

        {/* Input Field */}
        {tags.length < maxTags && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={tags.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
          />
        )}
      </div>

      {/* Tag Suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 mt-1">
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => addTag(suggestion)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
            >
              #{suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Helper Text */}
      <div className="flex justify-between items-center mt-1">
        <p className="text-xs text-gray-500">
          Press Enter or comma to add tags
        </p>
        <p className="text-xs text-gray-500">
          {tags.length}/{maxTags} tags
        </p>
      </div>
    </div>
  );
}
